import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { AppDispatch, RootState } from "app/store";
import { useDialog, useFetchUserData } from "app/hook";
import { actions as configActions } from "app/configSlice";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import { CloseBtn, ConfirmMessageBox, FadeTransition } from "common/elements";
import { ParentGate } from "features/parent";
import { usePlan, PlanType } from "features/subscription";
import { actions, selectAllChatRoomMessageSync } from "features/user/slice";
import { IconUserAdd, IconUserChange, IconUserAddInvalid } from "features/user/assets";
import { AppUser, UserType, AccountType, ChatRoomMessageSync } from "features/user/types";
import { UserList } from "./UserList";

const MessageUnread = ({
  uid,
  chatRoomMessageSync,
}: {
  uid: string;
  chatRoomMessageSync: ChatRoomMessageSync[];
}) =>
  chatRoomMessageSync.filter(
    (sync) =>
      sync.uid === uid &&
      (dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
        dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
      ) > 0 ||
        (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null)),
  ).length ? (
    <div className="flex-col-el flex-center absolute right-0 top-0 bg-danger rounded-full w-[20px] h-[20px] z-50" />
  ) : null;

const Account = ({
  user,
  handleOpen,
  chatRoomMessageSync,
}: {
  user: AppUser;
  handleOpen: () => void;
  chatRoomMessageSync: ChatRoomMessageSync[];
}) => (
  <div className="flex-col-center w-[92px] pointer-events-auto" onPointerDown={handleOpen}>
    <div
      className="flex-col-center cursor-pointer bg-white rounded-full w-[80px] h-[80px]"
      style={{
        boxShadow: "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
      }}
    >
      <MessageUnread uid={user.uid} chatRoomMessageSync={chatRoomMessageSync} />

      <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
        <img
          key={user.icon}
          alt="プレイヤープロフィール画像"
          src={user.icon}
          className="w-full h-full object-contain"
        />
      </div>
    </div>

    <div className="flex-col-center w-[90px] h-[24px]">
      <p className="text-body text-center text-base text-gray2-100 text-body-ellipsis w-full">
        {user.display_name}
      </p>
    </div>
  </div>
);

const AddUserRestrictedDialog = ({
  open,
  title,
  message,
  close,
  cancel,
  confirm,
  cancelText,
  confirmText,
}: {
  open: boolean;
  title: string;
  message: string;
  close: () => void;
  cancel: () => void;
  confirm: () => void;
  cancelText: string;
  confirmText: string;
}) => {
  const { scale } = useScale();
  return (
    <Dialog
      open={open}
      maxWidth={false}
      sx={{ zIndex: 10110 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "transparent" } },
      }}
      PaperProps={{
        style: {
          margin: 0,
          overflow: "visible",
          backgroundColor: "transparent",
        },
        elevation: 0,
      }}
    >
      <div
        className="flex-col-el flex-center relative"
        style={{
          filter:
            "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
        }}
      >
        <div className="flex-col-el flex-center relative" style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el justify-start w-[650px] min-h-[400px] relative border-solid border-white border-[8px] rounded-[8px] bg-white">
            <CloseBtn close={close} />

            <div className="flex-col-view justify-start w-full h-full flex-1 relative p-6 rounded-[4px] bg-beige-60">
              <div className="flex-row-view items-center justify-start pb-4">
                <div className="flex-col-center mr-2">
                  <span className="material-symbols-outlined !text-4xl text-[#4C9DF1]">info</span>
                </div>
                <p className="text-body text-textcolor-black text-[39px]">{title}</p>
              </div>
              <div className="flex-col-view items-start py-4 flex-1">
                {message?.split("\n").map((line, index) => (
                  <div key={index} className="flex-row-view flex-wrap mb-3">
                    <p className="text-body text-textcolor-black leading-8 text-xl">・</p>
                    <p className="text-body text-textcolor-black leading-8 text-xl flex-1">
                      {line}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex-row-el flex-center w-full gap-6 mt-4 alpha">
                <button className="btn btn-blue" onClick={cancel}>
                  <div className="flex-row-center gap-1 mb-[3px]">
                    <span className="material-symbols-outlined !text-4xl text-white">close</span>
                    <p className="text-body text-white text-font-size-body1 whitespace-nowrap">
                      {cancelText}
                    </p>
                  </div>
                </button>

                <button className="btn btn-primary" onClick={confirm}>
                  <div className="flex-row-center gap-1 mb-[3px]">
                    <span className="material-symbols-outlined !text-4xl text-white">
                      admin_panel_settings
                    </span>
                    <p className="text-body text-white text-font-size-body1 whitespace-nowrap">
                      {confirmText}
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export const UserAccount = () => {
  const play = usePlaySound();
  const navigate = useNavigate();
  const plan = usePlan();
  const handleDialog = useDialog();
  const [list, setList] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [change, setChange] = useState(false);
  const [addUserRestricted, setAddUserRestricted] = useState(false);
  const [parentGate, setParentGate] = useState(false);
  const { handleFetchUserData } = useFetchUserData();
  const [changeUser, setChangeUser] = useState<AppUser | null>(null);
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const active = useSelector((state: RootState) => state.user.active);
  const chatRoomMessageSync = useSelector(selectAllChatRoomMessageSync);
  const isEducation = plan === PlanType.EDUCATION;

  const handleShowUserList = () => {
    play();
    setList(true);
  };
  const handleCloseUserList = () => {
    play();
    setList(false);
  };

  const handleOpenAddNewUser = () => {
    play();
    btnDelay(() => {
      // 体験ユーザーの場合は課金促進ダイアログを表示
      if (plan === PlanType.TRIAL) {
        handleDialog({
          type: DialogType.RESTRICTED_FEATURE,
          value: true,
          args: { source: RestrictedFeatureSource.CHILD_USER_ADD_TRIAL },
        });
        return;
      }
      setAddUserRestricted(true);
    });
  };

  const handleCloseAddUserRestricted = () => {
    play();
    setAddUserRestricted(false);
  };

  const handleCancelAddUserRestricted = () => {
    play();
    btnDelay(() => {
      setAddUserRestricted(false);
    });
  };

  const handleGoToParentPage = () => {
    play();
    btnDelay(() => {
      setAddUserRestricted(false);
      if (!parent || parent.account_type === AccountType.GUEST) {
        handleDialog({
          type: DialogType.RESTRICTED_FEATURE,
          value: true,
          args: { source: RestrictedFeatureSource.PARENT_SETTINGS },
        });
        return;
      }
      setParentGate(true);
    });
  };

  const handleParentGateClose = () => {
    setParentGate(false);
  };

  const handleParentGateConfirm = () => {
    setParentGate(false);
    dispatch(
      configActions.updateDialog({
        type: DialogType.USER_PROFILE,
        value: false,
      }),
    );
    navigate("/parent?tab=player");
  };

  const handleOpenChangeUser = (user: AppUser) => () => {
    play();
    setChange(true);
    setChangeUser(user);
  };
  const handleChangeUser = () => {
    btnDelay(async () => {
      setChange(false);
      try {
        handleDialog({ type: DialogType.LOADING, value: true });
        handleFetchUserData({
          parent_uid: changeUser.parent_uid,
          uid: changeUser.uid,
        })
          .then(() => {
            dispatch(actions.setActiveUser(changeUser));
            dispatch(
              configActions.updateDialog({
                type: DialogType.USER_PROFILE,
                value: false,
              }),
            );
            handleDialog({ type: DialogType.LOADING, value: false });
            toast(
              <div>
                <p className="text text-textcolor-black">
                  {I18n.t("MSG_USER_PROFILE_CHANGE_USER_SUCCESS")}
                </p>
              </div>,
            );
            navigate("/top", { replace: true });
          })
          .catch((error) => {
            console.error(error);
            handleDialog({ type: DialogType.LOADING, value: false });
          });
      } catch (error) {
        console.error(error);
        console.error(JSON.stringify(changeUser));
        handleDialog({ type: DialogType.LOADING, value: false });
      }
    });
  };
  const handleChangeUserCancel = () => {
    play();
    setChange(false);
    setChangeUser(null);
  };

  return (
    <>
      <UserList visible={list} close={handleCloseUserList} />
      <AddUserRestrictedDialog
        open={addUserRestricted}
        title={I18n.t("MSG_USER_PROFILE_ADD_USER_DISABLED_TITLE")}
        message={I18n.t("MSG_USER_PROFILE_ADD_USER_DISABLED_MESSAGE")}
        close={handleCloseAddUserRestricted}
        cancel={handleCancelAddUserRestricted}
        confirm={handleGoToParentPage}
        cancelText={I18n.t("MSG_BTN_LATER")}
        confirmText={I18n.t("MSG_UPGRADE_PROMOTION_UPGRADE_BTN")}
      />
      {change && (
        <ConfirmMessageBox
          title={I18n.t("MSG_USER_PROFILE_CHANGE_USER_TITLE")}
          message={I18n.t("MSG_USER_PROFILE_CHANGE_USER_MESSAGE", {
            name: changeUser.display_name,
          })}
          close={handleChangeUserCancel}
          confirm={handleChangeUser}
        />
      )}

      <div className="flex-col-view justify-start items-end w-[416px] h-[168px]">
        <div className="flex-col-center w-[370px] overflow-hidden">
          <div className="flex-row-center px-[8px] h-[28px] bg-orange-alt rounded-[14px]">
            <IconUserChange />
            <p className="text text-textcolor-black !text-base !px-2">
              {I18n.t("MSG_USER_PROFILE_CHANGE_USER")}
            </p>
          </div>

          <div className="flex-row-el flex-center mt-[20px] gap-1">
            {users.filter(
              (u) => u.type !== UserType.FROZEN && u.account_type !== AccountType.PARENTS,
            ).length > 4 ? (
              <button
                id="user-page-user-list-btn"
                onClick={handleShowUserList}
                className="opacity-button w-[92px]"
              >
                <div className="flex-col-center">
                  <span className="material-symbols-outlined text-[#FFD1A1] text-[64px]">
                    expand_circle_down
                  </span>
                </div>
                <p className="text-body text-gray2-100 text-base">
                  {I18n.t("MSG_USER_PROFILE_SHOW_USER_LIST_BTN")}
                </p>
              </button>
            ) : !isEducation ? (
              <button onClick={handleOpenAddNewUser} className="opacity-button w-[92px]">
                <div className="flex-col-center w-[80px] h-[80px]">
                  {active.account_type === AccountType.KIDS && users.length < 3 ? (
                    <IconUserAdd />
                  ) : (
                    <IconUserAddInvalid />
                  )}
                </div>
                <p className="text-body text-gray2-100 text-base">
                  {I18n.t("MSG_USER_PROFILE_ADD_USER_BTN")}
                </p>
              </button>
            ) : null}

            {users
              .filter(
                (u) =>
                  u.uid !== active.uid &&
                  u.type !== UserType.FROZEN &&
                  u.account_type !== AccountType.PARENTS,
              )
              .sort((a, b) => {
                return dayjs(a.created_at) < dayjs(b.created_at) ? 1 : -1;
              })
              .slice(0, 3)
              .map((user) => (
                <Account
                  key={user.uid}
                  user={user}
                  handleOpen={handleOpenChangeUser(user)}
                  chatRoomMessageSync={chatRoomMessageSync}
                />
              ))}
          </div>
        </div>
      </div>

      <ParentGate
        visible={parentGate}
        close={handleParentGateClose}
        confirm={handleParentGateConfirm}
      />
    </>
  );
};
